import { X, Plus, CalendarDays } from "lucide-react"
import {
  SCHEDULE_TYPE_META,
  formatScheduleRange,
  formatAttendees,
  getScheduleTitle,
  type TeamSchedule,
} from "../hooks/useTeamSchedules"
import { getHolidayName, isHoliday } from "../lib/koreanHolidays"

interface ScheduleDayDetailModalProps {
  dateKey: string
  schedules: TeamSchedule[]
  onClose: () => void
  onSelectSchedule: (schedule: TeamSchedule) => void
  onCreate: () => void
}

/** 특정 날짜의 전체 일정 리스트 */
export function ScheduleDayDetailModal({
  dateKey,
  schedules,
  onClose,
  onSelectSchedule,
  onCreate,
}: ScheduleDayDetailModalProps) {
  const date = new Date(`${dateKey}T00:00:00`)
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"]
  const holidayName = isHoliday(dateKey) ? getHolidayName(dateKey) : ""

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">
              {date.getFullYear()}년 {date.getMonth() + 1}월 {date.getDate()}일 (
              {dayNames[date.getDay()]})
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {holidayName ? (
                <span className="font-medium text-rose-600">{holidayName}</span>
              ) : (
                `일정 ${schedules.length}건`
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="닫기"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {schedules.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
              <CalendarDays className="h-10 w-10 text-gray-200" />
              <p className="text-sm text-gray-500">등록된 일정이 없습니다.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {schedules.map((schedule) => {
                const meta = SCHEDULE_TYPE_META[schedule.scheduleType]
                return (
                  <li key={schedule._id}>
                    <button
                      type="button"
                      onClick={() => onSelectSchedule(schedule)}
                      className="w-full rounded-xl border border-gray-200 p-3 text-left transition hover:border-[#F59E0B]/50 hover:bg-orange-50/40"
                    >
                      <div className="mb-1 flex items-center gap-2">
                        <span
                          className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold ${meta?.chip ?? "bg-gray-100 text-gray-700 border-gray-200"}`}
                        >
                          {meta?.label ?? schedule.scheduleType}
                        </span>
                        <span className="truncate text-sm font-semibold text-gray-900">
                          {getScheduleTitle(schedule)}
                        </span>
                      </div>
                      <p className="truncate text-xs text-gray-600">
                        {formatAttendees(schedule.attendees)}
                      </p>
                      {schedule.startDate !== schedule.endDate && (
                        <p className="mt-1 text-[11px] text-gray-400">
                          {formatScheduleRange(schedule.startDate, schedule.endDate)}
                        </p>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onCreate}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#D97706]"
          >
            <Plus className="h-4 w-4" />
            이 날짜에 일정 등록
          </button>
        </div>
      </div>
    </div>
  )
}

interface ScheduleDetailModalProps {
  schedule: TeamSchedule
  canEdit: boolean
  onClose: () => void
  onEdit: () => void
}

/** 일정 1건 상세 */
export function ScheduleDetailModal({
  schedule,
  canEdit,
  onClose,
  onEdit,
}: ScheduleDetailModalProps) {
  const meta = SCHEDULE_TYPE_META[schedule.scheduleType]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={`shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${meta?.chip ?? "bg-gray-100 text-gray-700 border-gray-200"}`}
            >
              {meta?.label ?? schedule.scheduleType}
            </span>
            <h3 className="truncate text-base font-bold text-gray-900">
              {getScheduleTitle(schedule)}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="닫기"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div>
            <p className="mb-1 text-xs font-semibold text-gray-500">기간</p>
            <p className="text-sm text-gray-900">
              {formatScheduleRange(schedule.startDate, schedule.endDate)}
            </p>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-semibold text-gray-500">
              대상자 ({schedule.attendees.length}명)
            </p>
            <div className="flex flex-wrap gap-1.5">
              {schedule.attendees.map((a) => (
                <span
                  key={a.userId}
                  className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700"
                >
                  {a.userName}
                  {a.teamName ? (
                    <span className="text-[10px] text-orange-500">{a.teamName}</span>
                  ) : null}
                </span>
              ))}
            </div>
          </div>

          {schedule.description ? (
            <div>
              <p className="mb-1 text-xs font-semibold text-gray-500">내용</p>
              <p className="whitespace-pre-wrap break-words text-sm text-gray-700">
                {schedule.description}
              </p>
            </div>
          ) : null}

          <div className="border-t border-gray-100 pt-3">
            <p className="text-[11px] text-gray-400">
              등록자: {schedule.creatorName || "-"}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            닫기
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#D97706]"
            >
              수정
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
